import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  generatePath,
  NavLink,
  useLocation,
  useParams,
  Outlet,
} from 'react-router-dom';
import queryString from 'query-string';
import { useAppSelector, useAppDispatch } from '@store';
import {
  PermissionDenied,
  PermissionEnum,
  IconChain,
  Autocomplete,
  SelectSize,
  useQueryState,
  useGetList,
  useFilters,
  useScroll,
  SchemaFilterType,
  usePrevious,
  formatDate,
  Table,
  Filters,
} from '@ui/src';
import {
  useLazyGetPspPartnerLinkedClientsQuery,
  useLazyGetPspPartnerQuery,
} from './store/services';
import Card from '../../components/partial/card/card';
import useGetOrganizations from './helpers/use-get-organizations';
import {
  columnsItems,
  columnsLabels,
  filtersSchema,
  schema,
} from './variables/linked-clients';
import { PspPartnersLinkedClient } from './types';
import {
  clearLinkedClientsState,
  setLinkedClientsTakeCount,
} from './store/slices/info';

import { BankRoutes } from '../../routes';

import cn from './bank-and-psp.module.css';

const formatLinkedClientsList = (
  list?: PspPartnersLinkedClient[],
): PspPartnersLinkedClient[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, any> = {
      createdAt: <div>{formatDate(result.createdAt)}</div>,
      active: <div>{result.active ? 'Active' : 'Inactive'}</div>,
      organizationType: (
        <div>
          {`${result.organizationType.charAt(0)}${result.organizationType.slice(1).toLowerCase()}`}
        </div>
      ),
      organizationId: (
        <div>
          <NavLink
            to={`/customers/${result.organizationType === 'INDIVIDUAL' ? 'individual' : 'corporate'}/${result.organizationId}`}
            target="_blank"
            className={cn.linkedClientOrganizationLink}
          >
            {result.organizationType === 'INDIVIDUAL'
              ? `${result?.organization?.firstName || ''} ${result?.organization?.lastName || ''}`
              : result?.organization?.companyName || ''}
          </NavLink>
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList as PspPartnersLinkedClient[];
};

const LinkedCustomers = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [takeCount, setTakeCount] = useState(0);

  const { bankId } = useParams();
  const { search } = useLocation();

  const dispatch = useAppDispatch();

  const [getLinkedClients, { isLoading, isFetching }] =
    useLazyGetPspPartnerLinkedClientsQuery();
  const [
    getPartner,
    { isLoading: partnerIsLoading, isFetching: partnerIsFetching },
  ] = useLazyGetPspPartnerQuery();

  const { activePermissions } = useAppSelector(
    (state) => state.configurationDictionaries,
  );
  const {
    generalInfo,
    linkedClients,
    linkedClientsPagination,
    linkedClientsErrorsList,
  } = useAppSelector((state) => state.bankAndPsp.info);

  const { filters, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const { getListAction, onFilter, setPage, errorList } =
    useGetList<PspPartnersLinkedClient>({
      getList: getLinkedClients,
      onSortChange,
      onFilterChange,
      schema,
      itemId: bankId,
    });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const {
    autocompleteList,
    onSearch,
    isLoading: organizationsIsLoading,
    onClear,
    getOrganizationById,
    selectedValue,
    setSelectedValue,
  } = useGetOrganizations();

  const getPartnerAction = useCallback(() => {
    getPartner(bankId as string);
  }, [bankId]);

  const formattedData = useMemo(() => {
    const data = formatLinkedClientsList(linkedClients);

    return data;
  }, [filters, linkedClients]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (bankId && activePermissions?.includes(PermissionEnum.PSP_LIST)) {
      if (!generalInfo) {
        getPartnerAction();
      }
    }
  }, [bankId, generalInfo]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        setTakeCount(Math.ceil(take + 3));
        dispatch(setLinkedClientsTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      bankId &&
      !linkedClients &&
      !search &&
      !Object.keys(filters).length &&
      takeCount !== 0 &&
      activePermissions?.includes(PermissionEnum.EXTERNAL_ORGANIZATIONS_LIST)
    ) {
      getListAction({
        filters: {
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [filters, search, linkedClients, takeCount, bankId, activePermissions]);

  useEffect(() => {
    if (
      bankId &&
      takeCount !== 0 &&
      !linkedClients &&
      search &&
      filters &&
      prevFilters &&
      Object.keys(prevFilters).length &&
      activePermissions?.includes(PermissionEnum.EXTERNAL_ORGANIZATIONS_LIST)
    ) {
      const filtersValues: any = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: filtersValues,
      });

      if (autocompleteList && search) {
        const parsed = queryString.parse(search);

        if (parsed && parsed.organizationId) {
          if (
            !autocompleteList?.find(
              (item) => item.value === parsed.organizationId,
            )
          ) {
            getOrganizationById(parsed.organizationId as string);
          } else if (
            autocompleteList?.find(
              (item) => item.value === parsed.organizationId,
            )
          ) {
            setSelectedValue(parsed.organizationId as string);
          }
        }
      }
    }
  }, [
    filters,
    search,
    linkedClients,
    prevFilters,
    takeCount,
    bankId,
    activePermissions,
    autocompleteList,
  ]);

  useEffect(
    () => () => {
      dispatch(clearLinkedClientsState());
    },
    [],
  );

  let content;

  if (
    !activePermissions?.includes(PermissionEnum.PSP_LIST) ||
    (activePermissions?.includes(PermissionEnum.PSP_LIST) &&
      !activePermissions?.includes(PermissionEnum.EXTERNAL_ORGANIZATIONS_LIST))
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <div className={cn.externalBottom} ref={scrollRef}>
        <Card
          className={cn.documentsCard}
          wrapperClassName={cn.externalCard}
          isLoading={isLoading || partnerIsLoading || partnerIsFetching}
          title={
            <div
              className={`${cn.externalAccountsTitle} ${cn.linkedClientsTitle}`}
            >
              <div className={cn.externalAccountLeft}>
                <span className={cn.externalAccountsTitleText}>
                  {`${generalInfo?.companyName} - Linked Customers`}
                </span>
                {activePermissions.includes(
                  PermissionEnum.EXTERNAL_ORGANIZATIONS_CREATE_ACTION,
                ) && (
                  <NavLink
                    to={`${generatePath(
                      BankRoutes.BANK_AND_PSP_LINKED_CUSTOMERS_CREATE,
                      {
                        bankId: bankId || '',
                      },
                    )}${search || ''}`}
                    className={cn.addAccountLink}
                  >
                    <IconChain width={16} height={16} />
                    Link External Customer
                  </NavLink>
                )}
              </div>
              <div className={cn.organizationsSearchWrapper}>
                <Autocomplete
                  name="organizationSearch"
                  value={selectedValue}
                  size={SelectSize.SMALL}
                  list={autocompleteList}
                  placeholder="Enter customer name or company name..."
                  isLoading={organizationsIsLoading}
                  className={cn.searchOrganizationInput}
                  onSearch={(text) => onSearch(text)}
                  onChange={(e) => {
                    const nextFilters: Filters = {
                      ...filters,
                      take: {
                        value: [takeCount.toString()],
                        type: SchemaFilterType.STRING,
                      },
                    };
                    if (e.target.value) {
                      nextFilters.organizationId = {
                        value: [e.target.value as string],
                        type: SchemaFilterType.STRING,
                      };

                      const organization = autocompleteList?.find(
                        (item) => item.value === e.target.value,
                      );

                      if (organization) {
                        setSelectedValue(organization.value as string);
                      }
                    } else if (nextFilters.organizationId) {
                      delete nextFilters.organizationId;
                    }

                    if (
                      e.target.value ||
                      (!e.target.value && filters.organizationId)
                    ) {
                      onFilter({
                        ...nextFilters,
                      });
                    }
                  }}
                  onClearClick={() => {
                    onClear();

                    if (filters.organizationId) {
                      const nextFilters: Filters = {
                        ...filters,
                        take: {
                          value: [takeCount.toString()],
                          type: SchemaFilterType.STRING,
                        },
                      };

                      delete nextFilters.organizationId;

                      onFilter({
                        ...nextFilters,
                      });
                    }
                  }}
                  fullWidth
                  withClearButton
                />
              </div>
            </div>
          }
        >
          <Table
            columns={columnsItems}
            data={formattedData || []}
            onSort={() => {}}
            sort={{}}
            filters={filters}
            error={linkedClientsErrorsList || errorList}
            columnsLabels={columnsLabels}
            onFilterChange={(values) => {
              const nextFilters: Filters = {
                ...values,
                take: {
                  value: [takeCount.toString()],
                  type: SchemaFilterType.STRING,
                },
              };

              if (!values.organizationId && filters.organizationId) {
                nextFilters.organizationId = {
                  value: [filters.organizationId.value[0]],
                  type: SchemaFilterType.STRING,
                };
              }

              onFilter({
                ...nextFilters,
              });
            }}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isLoading || isFetching || takeCount === 0}
            storeKey="statements-of-accounts"
            pagination={linkedClientsPagination}
            tableBodyClassName={isLoading || isFetching ? cn.tableLoading : ''}
            tableTopClassName={cn.externalAccountsTableTop}
            take={takeCount.toString()}
            setPage={(data) => {
              setPage({
                ...data,
                filters: {
                  ...filters,
                  take: {
                    value: [takeCount.toString()],
                    type: SchemaFilterType.STRING,
                  },
                },
              });
            }}
            withScroll
          />
        </Card>
      </div>
    );
  }

  return (
    <>
      {content}
      <Outlet />
    </>
  );
};

export default LinkedCustomers;
